"use client";

/**
 * « Laboratoire le plus proche de moi » : géolocalisation du navigateur, puis
 * POST /api/laboratoires/proches. En cas de refus, de délai dépassé ou
 * d'absence de résultat, on propose les villes (jamais de page vide).
 *
 * Vie privée (revue sécurité du 30/09/2026) : la position EXACTE ne quitte
 * jamais le navigateur. Le serveur reçoit une position arrondie à 2 décimales
 * (≈ 1 km), dans le CORPS d'une requête POST — jamais dans l'URL, donc ni dans
 * les journaux nginx / CDN ni dans le fil d'Ariane de Sentry. Il cherche dans
 * un rayon élargi ; la distance exacte, le filtre à 25 km et le tri sont
 * calculés ici, à partir de la position exacte. enableHighAccuracy: false.
 *
 * Mise en page : dès le clic, la zone de résultats réserve sa hauteur (CLS) ;
 * seule une courte ligne de statut est annoncée (aria-live), pas les cartes.
 */
import { useState } from "react";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { Link } from "@/lib/i18n/navigation";
import { LabCard, type LabCardData } from "@/components/labs/LabCard";
import { NEAR_ME_MAX_RESULTS, NEAR_ME_RADIUS_KM, nearestWithin, roundForPrivacy } from "@/lib/labs/geo";

const LabMapView = dynamic(() => import("@/components/labs/LabMapView").then((m) => m.LabMapView), {
  ssr: false,
  loading: () => <div className="h-[380px] w-full animate-pulse rounded-[18px] bg-[var(--sahha-surface-1)]" aria-hidden="true" />,
});

type Phase = "idle" | "locating" | "loading" | "done" | "denied" | "timeout" | "unavailable" | "error";
type Props = { locale: string; cities: Array<{ href: string; name: string; count: number }> };

export function NearMeLabs({ locale, cities }: Props) {
  const t = useTranslations("labs");
  const [phase, setPhase] = useState<Phase>("idle");
  const [pos, setPos] = useState<{ lat: number; lng: number } | null>(null);
  const [items, setItems] = useState<Array<LabCardData & { distanceKm: number }>>([]);
  const busy = phase === "locating" || phase === "loading";

  const run = () => {
    if (busy) return;
    if (!("geolocation" in navigator)) { setPhase("unavailable"); return; }
    setPhase("locating");
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        const here = { lat: p.coords.latitude, lng: p.coords.longitude };
        setPhase("loading");
        try {
          const r = await fetch("/api/laboratoires/proches", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ lat: roundForPrivacy(here.lat), lng: roundForPrivacy(here.lng), locale }),
            cache: "no-store",
          });
          const d = (await r.json()) as { ok: boolean; items?: LabCardData[] };
          if (!r.ok || !d.ok) throw new Error(String(r.status));
          setPos(here);
          setItems(nearestWithin(d.items ?? [], here, NEAR_ME_RADIUS_KM, NEAR_ME_MAX_RESULTS));
          setPhase("done");
        } catch {
          setPhase("error");
        }
      },
      (e) => setPhase(e.code === e.PERMISSION_DENIED ? "denied" : e.code === e.TIMEOUT ? "timeout" : "unavailable"),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
    );
  };

  const status =
    phase === "locating" ? t("nearMe.locating")
    : phase === "loading" ? t("nearMe.searching")
    : phase === "done" ? (items.length ? t("nearMe.results", { count: items.length, km: NEAR_ME_RADIUS_KM }) : t("nearMe.none", { km: NEAR_ME_RADIUS_KM }))
    : phase === "denied" ? t("nearMe.denied")
    : phase === "timeout" ? t("nearMe.timeout")
    : phase === "unavailable" ? t("nearMe.unavailable")
    : phase === "error" ? t("nearMe.error")
    : "";
  const showPicker = phase === "idle" || phase === "denied" || phase === "timeout" || phase === "unavailable" || phase === "error" || (phase === "done" && items.length === 0);

  return (
    <div className="space-y-4">
      <button type="button" onClick={run} aria-disabled={busy}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--sahha-primary-700)] px-6 py-3.5 text-[15.5px] font-bold text-white shadow-[var(--sahha-shadow-md)] transition-colors hover:bg-[var(--sahha-primary-800)] aria-disabled:opacity-80 sm:w-auto">
        <span aria-hidden="true" className="relative inline-flex h-2.5 w-2.5">
          {busy && <span className="absolute inset-0 animate-ping rounded-full bg-white opacity-60 motion-reduce:hidden" />}
          <span className="relative h-2.5 w-2.5 rounded-full bg-[var(--sahha-accent-300)]" />
        </span>
        {phase === "idle" || busy ? t("nearMe.button") : t("nearMe.retry")}
      </button>

      {/* Seule zone annoncée : une ligne courte, toujours présente. */}
      <p role="status" className="min-h-6 text-[14px] font-semibold text-[var(--sahha-text-700)]">{status}</p>

      {/* Hauteur réservée dès le clic, dans tous les états suivants : les résultats arrivent sans décaler la page. */}
      <div className={phase === "idle" ? "" : "min-h-[640px]"}>
        {busy && (
          <div className="space-y-4" aria-hidden="true">
            <div className="h-[380px] w-full animate-pulse rounded-[18px] bg-[var(--sahha-surface-1)]" />
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="h-[200px] animate-pulse rounded-[18px] bg-[var(--sahha-surface-1)]" />
              <div className="hidden h-[200px] animate-pulse rounded-[18px] bg-[var(--sahha-surface-1)] sm:block" />
            </div>
          </div>
        )}
        {/* Enregistrement de session déjà coupé sur cette page (PostHogProvider) ; en plus, carte
            exclue (ph-no-capture) et textes masqués : ils situent le visiteur. */}
        {phase === "done" && items.length > 0 && (
          <section aria-labelledby="labs-near-me-results" className="space-y-4">
            <h2 id="labs-near-me-results" className="sr-only">{t("filters.listTitle")}</h2>
            {pos && <div className="ph-no-capture"><LabMapView labs={items} centerLat={pos.lat} centerLng={pos.lng} userPos={pos} /></div>}
            <ul data-ph-mask="" className="grid gap-3 sm:grid-cols-2">{items.map((l) => <li key={l.slug}><LabCard lab={l} locale={locale} /></li>)}</ul>
          </section>
        )}
        {showPicker && (
          <nav aria-labelledby="labs-city-picker">
            <p id="labs-city-picker" className="text-[13.5px] font-semibold text-[var(--sahha-text-600)]">{phase === "idle" ? t("nearMe.chooseCity") : t("nearMe.pickCity")}</p>
            <ul className="mt-3 flex flex-wrap gap-2">
              {cities.map((c) => (
                <li key={c.href}>
                  <Link href={c.href} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--sahha-surface-0)] px-3.5 py-1.5 text-[13.5px] font-semibold text-[var(--sahha-text-800)] ring-1 ring-[var(--sahha-border-default)] hover:bg-[var(--sahha-surface-1)]">
                    {c.name} <span className="tabular-nums text-[var(--sahha-text-500)]">{c.count}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </div>
  );
}
